// ============================================
// PAGE - Ingresar Producto
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSearch, MdAdd, MdCameraAlt } from 'react-icons/md';
import { useBarcodeScan } from '@/hooks/useBarcodeScan';
import { useScanBarcode } from '@almacenes/core';
import { BarcodeScannerModal } from '@/components/BarcodeScannerModal';

export const IngresarProductoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [codigoBarraInput, setCodigoBarraInput] = useState('');
  const [scanDetected, setScanDetected] = useState(false);
  const [codigoBusqueda, setCodigoBusqueda] = useState('');
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);

  // Obtener mensaje de éxito del state (si viene de guardar producto)
  useEffect(() => {
    if (location.state?.mensajeExito) {
      setMensajeExito(location.state.mensajeExito);
      setTimeout(() => setMensajeExito(null), 5000);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Hook para buscar producto por código de barras
  const { data: producto, isLoading, error } = useScanBarcode(codigoBusqueda);

  // Redirigir automáticamente cuando se encuentra el producto
  useEffect(() => {
    if (producto && !isLoading) {
      navigate('/inventario/ingresar/detalle', {
        state: { producto }
      });
    }
  }, [producto, isLoading, navigate]);

  // Hook para detectar escaneos de código de barras de forma global
  useBarcodeScan({
    onScan: (barcode) => {
      console.log('Código escaneado:', barcode);
      setCodigoBarraInput(barcode);
      setScanDetected(true);
      
      // Ocultar indicador después de 2 segundos
      setTimeout(() => setScanDetected(false), 2000);
      
      // Buscar automáticamente cuando se escanea
      setCodigoBusqueda(barcode);
    },
    enabled: true,
    minLength: 2,
  });

  const handleVolver = () => {
    navigate('/inventario');
  };

  const handleBuscar = () => {
    const codigoBarra = codigoBarraInput.trim();
    if (codigoBarra) {
      setCodigoBusqueda(codigoBarra);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBuscar();
    }
  };

  const handleAgregarSinCodigo = () => {
    // Navegar a detalle con producto vacío
    navigate('/inventario/ingresar/detalle', {
      state: {
        producto: {
          id: null,
          codigo: '',
          nombre: '',
          marca: '',
          esRetail: false,
          posibleRetail: false,
          imagenUrl: '',
          imagenProductoId: undefined
        }
      }
    });
  };

  return (
    <div className="min-h-screen">
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Contenedor centrado */}
        <div className="max-w-4xl mx-auto">
          {/* Mensaje de éxito */}
          {mensajeExito && (
            <div 
              className="mb-4 p-4 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: '#10B981', color: 'white' }}
            >
              <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <span className="font-semibold">{mensajeExito}</span>
              </div>
              <button
                onClick={() => setMensajeExito(null)}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {/* Indicador de escaneo detectado */}
          {scanDetected && (
            <div 
              className="mb-4 p-4 rounded-xl flex items-center gap-3 animate-pulse"
              style={{ backgroundColor: '#10B981', color: 'white' }}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">¡Código escaneado correctamente!</span>
            </div>
          )}

          {/* Buscador de código de barra */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {/* Header dentro de la card */}
            <div className="flex items-center gap-4 mb-8">
              <button
                onClick={handleVolver}
                className="p-2 rounded-lg transition-all hover:bg-gray-200"
                style={{ color: '#11194F' }}
              >
                <MdArrowBack className="text-2xl" />
              </button>
              <h1 className="text-4xl font-bold" style={{ color: '#11194F' }}>
                Ingresar Producto
              </h1>
            </div>

            <div className="flex flex-col md:flex-row gap-3 mb-8">
              {/* Grupo Input + Botón Cámara */}
              <div className="flex flex-1 border-2 border-gray-300 rounded-xl overflow-hidden focus-within:border-primary transition-colors">
                <input
                  type="text"
                  value={codigoBarraInput}
                  onChange={(e) => {
                    // Solo permitir números
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setCodigoBarraInput(value);
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Escanear código de barra"
                  className="flex-1 px-4 sm:px-6 py-4 sm:py-5 text-lg sm:text-xl border-0 outline-none min-w-0"
                  autoFocus
                />
                <button
                  onClick={() => setShowBarcodeScanner(true)}
                  className="lg:hidden flex-shrink-0 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-center transition-all hover:opacity-90"
                  style={{ backgroundColor: '#11194F', color: 'white' }}
                >
                  <MdCameraAlt className="text-xl sm:text-2xl" />
                </button>
              </div>
              
              {/* Botón Buscar */}
              <button
                onClick={() => handleBuscar()}
                disabled={!codigoBarraInput.trim()}
                className="px-8 sm:px-12 py-4 sm:py-5 rounded-xl flex items-center justify-center gap-3 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-lg sm:text-xl font-semibold"
                style={{ backgroundColor: '#FACC15', color: '#11194F' }}
              >
                <MdSearch className="text-2xl sm:text-3xl" />
                <span>Buscar</span>
              </button>
            </div>

            {/* Estado de búsqueda y resultados */}
            {isLoading && (
              <div className="mb-6 p-6 rounded-xl bg-blue-50 border-2 border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-blue-700 font-semibold text-lg">
                    Buscando producto...
                  </span>
                </div>
              </div>
            )}

            {/* Producto encontrado */}
            {!isLoading && producto && (
              <div className="mb-6 p-6 rounded-xl border-2 bg-green-50 border-green-200">
                <div className="flex items-start gap-4">
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold text-lg text-green-700 mb-3">
                      ¡Producto encontrado!
                    </p>
                    
                    <div className="bg-white rounded-lg p-4 space-y-3">
                      {/* Imagen del producto */}
                      {producto.imagenUrl && (
                        <div className="flex justify-center">
                          <img 
                            src={producto.imagenUrl} 
                            alt={producto.nombre}
                            className="h-40 w-auto object-contain rounded-lg"
                          />
                        </div>
                      )}
                      
                      {/* Información del producto */}
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm text-gray-500">Código:</span>
                          <p className="font-mono font-semibold" style={{ color: '#11194F' }}>
                            {producto.codigo}
                          </p>
                        </div>
                        
                        <div>
                          <span className="text-sm text-gray-500">Nombre:</span>
                          <p className="font-semibold text-lg" style={{ color: '#11194F' }}>
                            {producto.nombre}
                          </p>
                        </div>
                        
                        <div>
                          <span className="text-sm text-gray-500">Marca:</span>
                          <p className="font-medium text-gray-700">
                            {producto.marca}
                          </p>
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          {producto.esRetail && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                              Retail
                            </span>
                          )}
                          {producto.posibleRetail && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full">
                              Posible Retail
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Producto no encontrado */}
            {!isLoading && error && (
              <div className="mb-6 p-6 rounded-xl border-2 bg-red-50 border-red-200">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold text-lg text-red-700">
                      {(() => {
                        // @ts-ignore - error de axios tiene response.status
                        const status = error?.statusCode;
                        if (status === 400) {
                          return `El código de barras "${codigoBusqueda}" es inválido`;
                        } else if (status === 404) {
                          return `No se encontró producto con código: ${codigoBusqueda}`;
                        } else {
                          return `Error al buscar el producto. Intenta nuevamente.`;
                        }
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Separador */}
            <div className="flex items-center gap-4 my-8">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-gray-500 text-2xl font-medium">ó</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            {/* Botón agregar sin código */}
            <button
              onClick={handleAgregarSinCodigo}
              className="w-full px-4 sm:px-8 py-5 sm:py-6 rounded-xl border-2 transition-all hover:bg-gray-50 flex items-center justify-center gap-3 text-lg sm:text-xl font-semibold"
              style={{ 
                borderColor: '#11194F',
                color: '#11194F'
              }}
            >
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0" style={{ backgroundColor: '#11194F' }}>
                <MdAdd className="text-2xl sm:text-3xl text-white" />
              </div>
              <span>Agregar sin Código de barra</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de escáner de código de barras */}
      <BarcodeScannerModal
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={(barcode) => {
          console.log('Código escaneado con cámara:', barcode);
          setCodigoBarraInput(barcode);
          setCodigoBusqueda(barcode);
          setShowBarcodeScanner(false);
          setScanDetected(true);
          setTimeout(() => setScanDetected(false), 2000);
        }}
      />
    </div>
  );
};
